import React, { useState } from "react";
import { Info, Phone, Facebook, Mail, ShieldAlert } from "lucide-react";

/*
  Shared, deliberately non-functional inquiry UI for accounting/bookkeeping/tax
  concept sites. Nothing is persisted, transmitted, or uploaded — these are
  unaffiliated concept demos and must never collect real business, tax or
  financial records. Only general-purpose fields are offered; there is no file
  upload, since business owners should never be invited to send financial
  statements, BIR forms or IDs through a preview form. Theming and contact
  fallbacks are passed in per business.
*/
export default function AccountingInquiryForm({
  testidPrefix,
  businessName,
  accent,
  accentText = "#FFFFFF",
  panelBg = "#FFFFFF",
  border = "#E5E5E5",
  muted = "#6B6B6B",
  heading = "Ask About Your Books",
  subheading = "Share a few general details and the team can follow up about how ongoing support would work.",
  servicesNeeded = ["Bookkeeping", "Tax Compliance", "Business Registration", "Payroll", "Audit / Advisory", "Not sure yet"],
  businessTypes = ["Sole Proprietorship", "Partnership", "Corporation", "Freelancer / Professional", "Not yet registered"],
  phone,
  phoneDisplay,
  messengerUrl,
  email,
}) {
  const [form, setForm] = useState({ name: "", businessNameField: "", contact: "", service: "", businessType: "", details: "" });
  const [submitted, setSubmitted] = useState(false);
  const tagalog = typeof window !== "undefined" && window.localStorage.getItem("mysitecare-language") === "tl";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div data-testid={`${testidPrefix}-inquiry-notice`} className="rounded-xl p-8 text-center" style={{ background: panelBg, border: `1px solid ${border}` }}>
        <Info size={36} style={{ color: accent }} className="mx-auto" />
        <p className="mt-4 text-base font-semibold" style={{ color: "#1A1A1A" }}>{tagalog ? "Konseptong preview lamang — hindi pa konektado ang form na ito." : "Concept preview only — this form is not currently connected."}</p>
        <p role="status" aria-live="polite" className="mt-2 text-sm" style={{ color: muted }}>{tagalog ? `Direktang makipag-ugnayan sa ${businessName} gamit ang nakumpirmang detalye sa ibaba.` : `Please contact ${businessName} directly using the verified details below.`}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {phone && (
            <a href={`tel:${phone}`} data-testid={`${testidPrefix}-inquiry-call`} className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold" style={{ background: accent, color: accentText }}>
              <Phone size={16} /> {phoneDisplay || phone}
            </a>
          )}
          {messengerUrl && (
            <a href={messengerUrl} target="_blank" rel="noopener noreferrer" data-testid={`${testidPrefix}-inquiry-messenger`} className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold border" style={{ borderColor: border, color: "#1A1A1A" }}>
              <Facebook size={16} /> {tagalog ? "Mag-message sa Facebook" : "Message on Facebook"}
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} data-testid={`${testidPrefix}-inquiry-email`} className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold border" style={{ borderColor: border, color: "#1A1A1A" }}>
              <Mail size={16} /> {email}
            </a>
          )}
        </div>
        <button onClick={() => setSubmitted(false)} data-testid={`${testidPrefix}-inquiry-reset`} className="mt-6 text-xs underline" style={{ color: muted }}>{tagalog ? "Punan ang isa pang inquiry" : "Fill out another inquiry"}</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid={`${testidPrefix}-inquiry-form`} className="rounded-xl p-6 sm:p-8 space-y-5" style={{ background: panelBg, border: `1px solid ${border}` }}>
      <div>
        <h3 className="text-2xl font-semibold" style={{ color: "#1A1A1A" }}>{heading}</h3>
        <p className="mt-2 text-sm" style={{ color: muted }}>{subheading}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${testidPrefix}-inquiry-name`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Buong Pangalan" : "Full Name"}</label>
          <input id={`${testidPrefix}-inquiry-name`} autoComplete="name" required data-testid={`${testidPrefix}-inquiry-name`} value={form.name} onChange={set("name")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={{ border: `1px solid ${border}` }} placeholder="Juan Dela Cruz" />
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-inquiry-bizname`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Pangalan ng Negosyo" : "Business Name"}</label>
          <input id={`${testidPrefix}-inquiry-bizname`} autoComplete="organization" data-testid={`${testidPrefix}-inquiry-bizname`} value={form.businessNameField} onChange={set("businessNameField")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={{ border: `1px solid ${border}` }} placeholder={tagalog ? "Pangalan ng inyong negosyo" : "Your business name"} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${testidPrefix}-inquiry-contact`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Telepono o Email" : "Phone or Email"}</label>
          <input id={`${testidPrefix}-inquiry-contact`} autoComplete="email" required data-testid={`${testidPrefix}-inquiry-contact`} value={form.contact} onChange={set("contact")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={{ border: `1px solid ${border}` }} placeholder="0917 000 0000" />
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-inquiry-biztype`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Uri ng Negosyo" : "Business Type"}</label>
          <select id={`${testidPrefix}-inquiry-biztype`} data-testid={`${testidPrefix}-inquiry-biztype`} value={form.businessType} onChange={set("businessType")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none bg-white" style={{ border: `1px solid ${border}` }}>
            <option value="">{tagalog ? "Pumili ng isa" : "Select one"}</option>
            {businessTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor={`${testidPrefix}-inquiry-service`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Kinakailangang Serbisyo" : "Service Needed"}</label>
        <select id={`${testidPrefix}-inquiry-service`} data-testid={`${testidPrefix}-inquiry-service`} value={form.service} onChange={set("service")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none bg-white" style={{ border: `1px solid ${border}` }}>
          <option value="">{tagalog ? "Pumili ng isa" : "Select one"}</option>
          {servicesNeeded.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div>
        <label htmlFor={`${testidPrefix}-inquiry-details`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Pangkalahatang Katanungan" : "General Inquiry"}</label>
        <textarea id={`${testidPrefix}-inquiry-details`} data-testid={`${testidPrefix}-inquiry-details`} value={form.details} onChange={set("details")} rows={4} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none resize-none" style={{ border: `1px solid ${border}` }} placeholder={tagalog ? "hal. 'Kailangan ng tulong na maayos ang books bago ang tax season' — hindi kailangan ang account number, TIN, o financial figures" : "e.g. 'Need help getting my books current before tax season' — no account numbers, TINs, or financial figures needed"} />
      </div>

      <div className="flex items-start gap-2.5 rounded-lg px-4 py-3 text-xs" style={{ background: "rgba(0,0,0,0.035)", color: muted }}>
        <ShieldAlert size={16} className="shrink-0 mt-0.5" style={{ color: accent }} />
        <span>{tagalog ? "Huwag magpadala ng tax documents, ID, BIR forms, o financial statements sa konseptong form na ito. Preview lamang ito — walang inilalagay dito na naiimbak, naipapadala, o sinusuri ninuman." : "Please do not send tax documents, IDs, BIR forms or financial statements through this concept form. It is a preview only — nothing you enter is stored, transmitted, or reviewed by anyone."}</span>
      </div>

      <button type="submit" data-testid={`${testidPrefix}-inquiry-submit`} className="w-full rounded-lg py-3.5 text-sm font-semibold transition-transform hover:-translate-y-0.5" style={{ background: accent, color: accentText }}>
        {tagalog ? "Ipadala ang Inquiry" : "Send Inquiry"}
      </button>
      <p className="text-[11px] text-center" style={{ color: muted }}>{tagalog ? "Konseptong preview form lamang ito. Hindi nagpapadala ng tunay na inquiry ang pagsumite nito." : "This is a concept preview form. Submitting it does not send a real inquiry."}</p>
    </form>
  );
}
